import { ThemeColor } from './storage';

export interface ThemeStyles {
  // Main app page background & text
  appBg: string;
  appText: string;
  
  // Primary text color for numbers & badges
  textAccent: string;
  textAccentDark: string;
  textScore: string;
  
  // Badges & pills
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
  
  // Surfaces & Cards
  cardBg: string;
  cardBgActive: string;
  cardText: string;
  cardTextSecondary: string;
  cardTextActive: string;
  cardBorderActive: string;
  cardBorderSubtle: string;
  
  // Keypad & Controls
  keypadContainerBg: string;
  keypadPrimaryBg: string;
  keypadPrimaryText: string;
  keypadPrimaryBorder: string;
  keypadNumberBg: string;
  keypadNumberText: string;
  keypadNumberActiveBg: string;
  keypadNumberHover: string;
  keypadPresetBg: string;
  keypadPresetText: string;
  
  // Multiplier 2x/3x buttons
  multiplierActiveBg: string;
  multiplierActiveText: string;
  multiplierInactiveText: string;
  multiplierInactiveBg: string;
  multiplierInactiveBorder: string;

  // Bull & DBull buttons
  bullBg: string;
  bullText: string;
  bullBorder: string;
  dbullBg: string;
  dbullText: string;
  dbullBorder: string;

  // Header / Navbar
  navBg: string;
  navText: string;
  navBrandGlow: string;
  navIconColor: string;
  navButtonBg: string;
  navButtonText: string;

  // Modals
  modalOverlay: string;
  modalBg: string;
  modalText: string;
  modalTextMuted: string;
  modalCardBg: string;
  modalFooterBg: string;
  modalBorder: string;
}

export const THEME_MAP_DARK: Record<ThemeColor, ThemeStyles> = {
  emerald: {
    appBg: 'bg-[#0A0A0A]',
    appText: 'text-white',
    textAccent: 'text-emerald-400',
    textAccentDark: 'text-emerald-500',
    textScore: 'text-emerald-400',
    badgeBg: 'bg-emerald-500/20',
    badgeText: 'text-emerald-300 font-bold',
    badgeBorder: 'border-emerald-500/40',
    cardBg: 'bg-[#161616] border border-white/10 shadow-sm',
    cardBgActive: 'bg-emerald-950/60 border border-emerald-500/60 shadow-md ring-2 ring-emerald-500/50',
    cardText: 'text-white',
    cardTextSecondary: 'text-slate-300',
    cardTextActive: 'text-emerald-100',
    cardBorderActive: 'ring-2 ring-emerald-500/60',
    cardBorderSubtle: 'border-white/10',
    keypadContainerBg: 'bg-[#121212]/95 backdrop-blur-xl border border-white/10 shadow-2xl',
    keypadPrimaryBg: 'bg-emerald-500 hover:bg-emerald-400',
    keypadPrimaryText: 'text-black font-extrabold',
    keypadPrimaryBorder: 'border-emerald-400',
    keypadNumberBg: 'bg-[#222222] hover:bg-[#2C2C2C] border border-white/15 shadow-sm',
    keypadNumberText: 'text-white font-bold',
    keypadNumberActiveBg: 'active:bg-emerald-500/30',
    keypadNumberHover: 'hover:bg-[#2C2C2C]',
    keypadPresetBg: 'bg-[#222222] hover:bg-[#2C2C2C] border border-white/15',
    keypadPresetText: 'text-emerald-300 font-bold',
    multiplierActiveBg: 'bg-emerald-500',
    multiplierActiveText: 'text-black font-extrabold',
    multiplierInactiveText: 'text-emerald-400 font-bold',
    multiplierInactiveBg: 'bg-[#222222] hover:bg-[#2C2C2C] border border-emerald-500/40',
    multiplierInactiveBorder: 'border-emerald-500/40',
    bullBg: 'bg-[#222222] hover:bg-[#2C2C2C] border border-white/15',
    bullText: 'text-emerald-400 font-bold',
    bullBorder: 'border-white/20',
    dbullBg: 'bg-[#222222] hover:bg-[#2C2C2C] border border-white/15',
    dbullText: 'text-emerald-400 font-bold',
    dbullBorder: 'border-white/20',
    navBg: 'bg-[#121212]/90 shadow-md border-b border-white/10',
    navText: 'text-white',
    navBrandGlow: 'from-emerald-500 to-teal-400',
    navIconColor: 'text-emerald-400',
    navButtonBg: 'bg-[#222222] hover:bg-[#2C2C2C] border border-white/15 shadow-xs',
    navButtonText: 'text-white/90 hover:text-white font-semibold',
    modalOverlay: 'bg-black/60 backdrop-blur-xl',
    modalBg: 'bg-[#141414] border border-white/10 shadow-2xl',
    modalText: 'text-white',
    modalTextMuted: 'text-slate-400',
    modalCardBg: 'bg-[#1E1E1E] border border-white/10',
    modalFooterBg: 'bg-[#141414] border-t border-white/10',
    modalBorder: 'border-white/10'
  },
  dartboard: {
    appBg: 'bg-gradient-to-b from-[#180606] via-[#100303] to-[#0A0202]',
    appText: 'text-white',
    textAccent: 'text-red-400',
    textAccentDark: 'text-red-500',
    textScore: 'text-amber-400',
    badgeBg: 'bg-red-500/25',
    badgeText: 'text-red-300 font-bold',
    badgeBorder: 'border-red-500/40',
    cardBg: 'bg-[#1C0F0F] border border-red-950/60 shadow-sm',
    cardBgActive: 'bg-red-950/70 border border-red-500/60 shadow-md ring-2 ring-red-500/50',
    cardText: 'text-white',
    cardTextSecondary: 'text-slate-300',
    cardTextActive: 'text-red-100',
    cardBorderActive: 'ring-2 ring-red-500/60',
    cardBorderSubtle: 'border-red-950/60',
    keypadContainerBg: 'bg-[#140606]/95 backdrop-blur-xl border border-red-950/60 shadow-2xl',
    keypadPrimaryBg: 'bg-red-700 hover:bg-red-800',
    keypadPrimaryText: 'text-white font-extrabold',
    keypadPrimaryBorder: 'border-red-400',
    keypadNumberBg: 'bg-[#261313] hover:bg-[#331A1A] border border-red-900/40 shadow-sm',
    keypadNumberText: 'text-amber-200 font-bold',
    keypadNumberActiveBg: 'active:bg-red-500/30',
    keypadNumberHover: 'hover:bg-[#331A1A]',
    keypadPresetBg: 'bg-[#261313] hover:bg-[#331A1A] border border-red-900/40',
    keypadPresetText: 'text-amber-300 font-bold',
    multiplierActiveBg: 'bg-amber-500',
    multiplierActiveText: 'text-black font-extrabold',
    multiplierInactiveText: 'text-amber-400 font-bold',
    multiplierInactiveBg: 'bg-[#261313] hover:bg-[#331A1A] border border-amber-500/30',
    multiplierInactiveBorder: 'border-amber-500/30',
    bullBg: 'bg-[#14261B] hover:bg-[#1C3626] border border-emerald-500/40',
    bullText: 'text-emerald-400 font-bold',
    bullBorder: 'border-emerald-500/40',
    dbullBg: 'bg-[#261313] hover:bg-[#331A1A] border border-red-900/40',
    dbullText: 'text-red-400 font-bold',
    dbullBorder: 'border-red-900/40',
    navBg: 'bg-[#1A0808]/90 shadow-md border-b border-red-950/60',
    navText: 'text-white',
    navBrandGlow: 'from-red-500 via-amber-400 to-emerald-500',
    navIconColor: 'text-red-400',
    navButtonBg: 'bg-[#261313] hover:bg-[#331A1A] border border-red-900/40 shadow-xs',
    navButtonText: 'text-white/90 hover:text-white font-semibold',
    modalOverlay: 'bg-black/60 backdrop-blur-xl',
    modalBg: 'bg-[#1A0909] border border-red-950/60 shadow-2xl',
    modalText: 'text-white',
    modalTextMuted: 'text-slate-400',
    modalCardBg: 'bg-[#261313] border border-red-950/60',
    modalFooterBg: 'bg-[#1A0909] border-t border-red-950/60',
    modalBorder: 'border-red-950/60'
  },
  amber: {
    appBg: 'bg-[#0E0A04]',
    appText: 'text-white',
    textAccent: 'text-amber-400',
    textAccentDark: 'text-amber-500',
    textScore: 'text-amber-400',
    badgeBg: 'bg-amber-500/20',
    badgeText: 'text-amber-300 font-bold',
    badgeBorder: 'border-amber-500/40',
    cardBg: 'bg-[#1C150B] border border-amber-950/60 shadow-sm',
    cardBgActive: 'bg-amber-950/60 border border-amber-500/60 shadow-md ring-2 ring-amber-500/50',
    cardText: 'text-white',
    cardTextSecondary: 'text-slate-300',
    cardTextActive: 'text-amber-100',
    cardBorderActive: 'ring-2 ring-amber-500/60',
    cardBorderSubtle: 'border-amber-950/60',
    keypadContainerBg: 'bg-[#140E06]/95 backdrop-blur-xl border border-amber-950/60 shadow-2xl',
    keypadPrimaryBg: 'bg-amber-500 hover:bg-amber-400',
    keypadPrimaryText: 'text-black font-extrabold',
    keypadPrimaryBorder: 'border-amber-300',
    keypadNumberBg: 'bg-[#281E10] hover:bg-[#362916] border border-amber-800/40 shadow-sm',
    keypadNumberText: 'text-amber-100 font-bold',
    keypadNumberActiveBg: 'active:bg-amber-500/30',
    keypadNumberHover: 'hover:bg-[#362916]',
    keypadPresetBg: 'bg-[#281E10] hover:bg-[#362916] border border-amber-800/40',
    keypadPresetText: 'text-amber-300 font-bold',
    multiplierActiveBg: 'bg-amber-500',
    multiplierActiveText: 'text-black font-extrabold',
    multiplierInactiveText: 'text-amber-400 font-bold',
    multiplierInactiveBg: 'bg-[#281E10] hover:bg-[#362916] border border-amber-500/30',
    multiplierInactiveBorder: 'border-amber-500/30',
    bullBg: 'bg-[#281E10] hover:bg-[#362916] border border-amber-500/40',
    bullText: 'text-amber-400 font-bold',
    bullBorder: 'border-white/20',
    dbullBg: 'bg-[#281E10] hover:bg-[#362916] border border-amber-500/40',
    dbullText: 'text-amber-400 font-bold',
    dbullBorder: 'border-white/20',
    navBg: 'bg-[#170F07]/90 shadow-md border-b border-amber-950/60',
    navText: 'text-white',
    navBrandGlow: 'from-amber-400 to-yellow-500',
    navIconColor: 'text-amber-400',
    navButtonBg: 'bg-[#281E10] hover:bg-[#362916] border border-amber-800/40 shadow-xs',
    navButtonText: 'text-white/90 hover:text-white font-semibold',
    modalOverlay: 'bg-black/60 backdrop-blur-xl',
    modalBg: 'bg-[#170F07] border border-amber-950/60 shadow-2xl',
    modalText: 'text-white',
    modalTextMuted: 'text-slate-400',
    modalCardBg: 'bg-[#281E10] border border-amber-950/60',
    modalFooterBg: 'bg-[#170F07] border-t border-amber-950/60',
    modalBorder: 'border-amber-950/60'
  }
};

export const THEME_MAP_LIGHT: Record<ThemeColor, ThemeStyles> = {
  emerald: {
    appBg: 'bg-slate-50',
    appText: 'text-slate-900',
    textAccent: 'text-emerald-700',
    textAccentDark: 'text-emerald-800',
    textScore: 'text-emerald-700',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-950 font-bold',
    badgeBorder: 'border-emerald-300',
    cardBg: 'bg-white shadow-xs border border-slate-200/90',
    cardBgActive: 'bg-emerald-50 shadow-sm border border-emerald-300 ring-2 ring-emerald-600/50',
    cardText: 'text-slate-900',
    cardTextSecondary: 'text-slate-600',
    cardTextActive: 'text-emerald-950',
    cardBorderActive: 'ring-2 ring-emerald-600',
    cardBorderSubtle: 'border-slate-200/80',
    keypadContainerBg: 'bg-slate-100/90 backdrop-blur-xl shadow-md border border-slate-200/80',
    keypadPrimaryBg: 'bg-emerald-700 hover:bg-emerald-800',
    keypadPrimaryText: 'text-white font-extrabold',
    keypadPrimaryBorder: 'border-emerald-700',
    keypadNumberBg: 'bg-white hover:bg-slate-50 border border-slate-200/90 shadow-2xs',
    keypadNumberText: 'text-slate-900 font-bold',
    keypadNumberActiveBg: 'active:bg-emerald-100',
    keypadNumberHover: 'hover:bg-slate-50',
    keypadPresetBg: 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200',
    keypadPresetText: 'text-emerald-900 font-bold',
    multiplierActiveBg: 'bg-emerald-600',
    multiplierActiveText: 'text-white font-extrabold',
    multiplierInactiveText: 'text-emerald-900 font-bold',
    multiplierInactiveBg: 'bg-white hover:bg-emerald-50 border border-emerald-300',
    multiplierInactiveBorder: 'border-emerald-300',
    bullBg: 'bg-white hover:bg-slate-100 border border-slate-300 shadow-2xs',
    bullText: 'text-emerald-800 font-bold',
    bullBorder: 'border-slate-300',
    dbullBg: 'bg-white hover:bg-slate-100 border border-slate-300 shadow-2xs',
    dbullText: 'text-emerald-800 font-bold',
    dbullBorder: 'border-slate-300',
    navBg: 'bg-white/90 shadow-xs border-b border-slate-200/80',
    navText: 'text-slate-900',
    navBrandGlow: 'from-emerald-600 to-teal-600',
    navIconColor: 'text-emerald-700',
    navButtonBg: 'bg-slate-100 hover:bg-slate-200',
    navButtonText: 'text-slate-800 hover:text-slate-950 font-semibold',
    modalOverlay: 'bg-black/35 backdrop-blur-md',
    modalBg: 'bg-white shadow-2xl border border-slate-200',
    modalText: 'text-slate-900',
    modalTextMuted: 'text-slate-600',
    modalCardBg: 'bg-slate-50 border border-slate-200/80',
    modalFooterBg: 'bg-white border-t border-slate-200/80',
    modalBorder: 'border-slate-200/80'
  },
  dartboard: {
    appBg: 'bg-[#FAF6F6]',
    appText: 'text-slate-900',
    textAccent: 'text-red-700',
    textAccentDark: 'text-red-800',
    textScore: 'text-red-700',
    badgeBg: 'bg-red-100',
    badgeText: 'text-red-950 font-bold',
    badgeBorder: 'border-red-300',
    cardBg: 'bg-white shadow-xs border border-slate-200/90',
    cardBgActive: 'bg-red-50 shadow-sm border border-red-300 ring-2 ring-red-600/50',
    cardText: 'text-slate-900',
    cardTextSecondary: 'text-slate-600',
    cardTextActive: 'text-red-950',
    cardBorderActive: 'ring-2 ring-red-600',
    cardBorderSubtle: 'border-red-100',
    keypadContainerBg: 'bg-slate-100/90 backdrop-blur-xl shadow-md border border-slate-200/80',
    keypadPrimaryBg: 'bg-red-700 hover:bg-red-800',
    keypadPrimaryText: 'text-white font-extrabold',
    keypadPrimaryBorder: 'border-red-700',
    keypadNumberBg: 'bg-white hover:bg-slate-50 border border-slate-200/90 shadow-2xs',
    keypadNumberText: 'text-slate-900 font-bold',
    keypadNumberActiveBg: 'active:bg-red-100',
    keypadNumberHover: 'hover:bg-slate-50',
    keypadPresetBg: 'bg-red-50 hover:bg-red-100 border border-red-200',
    keypadPresetText: 'text-red-900 font-bold',
    multiplierActiveBg: 'bg-amber-600',
    multiplierActiveText: 'text-white font-extrabold',
    multiplierInactiveText: 'text-amber-900 font-bold',
    multiplierInactiveBg: 'bg-white hover:bg-amber-50 border border-amber-300',
    multiplierInactiveBorder: 'border-amber-300',
    bullBg: 'bg-white hover:bg-emerald-50 border border-emerald-300 shadow-2xs',
    bullText: 'text-emerald-800 font-bold',
    bullBorder: 'border-emerald-300',
    dbullBg: 'bg-white hover:bg-red-50 border border-red-300 shadow-2xs',
    dbullText: 'text-red-800 font-bold',
    dbullBorder: 'border-red-300',
    navBg: 'bg-white/90 shadow-xs border-b border-red-100',
    navText: 'text-slate-900',
    navBrandGlow: 'from-red-600 to-amber-600',
    navIconColor: 'text-red-700',
    navButtonBg: 'bg-slate-100 hover:bg-slate-200',
    navButtonText: 'text-slate-800 hover:text-slate-950 font-semibold',
    modalOverlay: 'bg-black/35 backdrop-blur-md',
    modalBg: 'bg-white shadow-2xl border border-slate-200',
    modalText: 'text-slate-900',
    modalTextMuted: 'text-slate-600',
    modalCardBg: 'bg-red-50/50 border border-red-100',
    modalFooterBg: 'bg-white border-t border-red-100',
    modalBorder: 'border-red-100'
  },
  amber: {
    appBg: 'bg-[#FAF8F3]',
    appText: 'text-slate-900',
    textAccent: 'text-amber-800',
    textAccentDark: 'text-amber-900',
    textScore: 'text-amber-800',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-950 font-bold',
    badgeBorder: 'border-amber-300',
    cardBg: 'bg-white shadow-xs border border-slate-200/90',
    cardBgActive: 'bg-amber-50 shadow-sm border border-amber-300 ring-2 ring-amber-600/50',
    cardText: 'text-slate-900',
    cardTextSecondary: 'text-slate-600',
    cardTextActive: 'text-amber-950',
    cardBorderActive: 'ring-2 ring-amber-600',
    cardBorderSubtle: 'border-amber-100',
    keypadContainerBg: 'bg-slate-100/90 backdrop-blur-xl shadow-md border border-slate-200/80',
    keypadPrimaryBg: 'bg-amber-700 hover:bg-amber-800',
    keypadPrimaryText: 'text-white font-extrabold',
    keypadPrimaryBorder: 'border-amber-700',
    keypadNumberBg: 'bg-white hover:bg-slate-50 border border-slate-200/90 shadow-2xs',
    keypadNumberText: 'text-slate-900 font-bold',
    keypadNumberActiveBg: 'active:bg-amber-100',
    keypadNumberHover: 'hover:bg-slate-50',
    keypadPresetBg: 'bg-amber-50 hover:bg-amber-100 border border-amber-200',
    keypadPresetText: 'text-amber-950 font-bold',
    multiplierActiveBg: 'bg-amber-600',
    multiplierActiveText: 'text-white font-extrabold',
    multiplierInactiveText: 'text-amber-950 font-bold',
    multiplierInactiveBg: 'bg-white hover:bg-amber-50 border border-amber-300',
    multiplierInactiveBorder: 'border-amber-300',
    bullBg: 'bg-white hover:bg-amber-50 border border-amber-300 shadow-2xs',
    bullText: 'text-amber-900 font-bold',
    bullBorder: 'border-amber-300',
    dbullBg: 'bg-white hover:bg-amber-50 border border-amber-300 shadow-2xs',
    dbullText: 'text-amber-900 font-bold',
    dbullBorder: 'border-amber-300',
    navBg: 'bg-white/90 shadow-xs border-b border-amber-100',
    navText: 'text-slate-900',
    navBrandGlow: 'from-amber-600 to-yellow-600',
    navIconColor: 'text-amber-800',
    navButtonBg: 'bg-slate-100 hover:bg-slate-200',
    navButtonText: 'text-slate-800 hover:text-slate-950 font-semibold',
    modalOverlay: 'bg-black/35 backdrop-blur-md',
    modalBg: 'bg-white shadow-2xl border border-slate-200',
    modalText: 'text-slate-900',
    modalTextMuted: 'text-slate-600',
    modalCardBg: 'bg-amber-50/50 border border-amber-100',
    modalFooterBg: 'bg-white border-t border-amber-100',
    modalBorder: 'border-amber-100'
  }
};

export function getTheme(themeColor?: ThemeColor, isDark: boolean = true): ThemeStyles {
  const color = themeColor || 'emerald';
  return isDark
    ? (THEME_MAP_DARK[color] || THEME_MAP_DARK.emerald)
    : (THEME_MAP_LIGHT[color] || THEME_MAP_LIGHT.emerald);
}

